import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customFetch } from '../apiClient';

const VendorDTForm = () => {
  const [form, setForm] = useState({
    vendorName: "", gstin: "", address: "", stateName: "",
    stateCode: "", mobile: "", emailId: "", opBal: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [vendors, setVendors] = useState([]);

  // Modal Edit States
  const [showEditModal, setShowEditModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [editForm, setEditForm] = useState({
    vendorName: "", gstin: "", address: "", stateName: "",
    stateCode: "", mobile: "", emailId: "", opBal: ""
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  const formRef = useRef(null);
  const editFormRef = useRef(null);
  const navigate = useNavigate();

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  useEffect(() => {
    if (editError) { const timer = setTimeout(() => setEditError(""), 4000); return () => clearTimeout(timer); }
  }, [editError]);

  useEffect(() => {
    loadVendors();
    if (formRef.current) {
      const firstInput = formRef.current.querySelector('input:not([disabled]):not([type="hidden"]), select, textarea');
      firstInput?.focus();
    }
  }, []);

 const loadVendors = async () => {
    try {
      const res = await customFetch('/VendorDT');
      if (res.ok) setVendors(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  // Keyboard Navigation
  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit(); return;
    }
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) handleKeyDown(e);
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendorName?.trim()) { setError("Vendor Name is required."); return; }

    setLoading(true); setError(""); setSuccess("");
    try {
    
      const formData = new FormData();
      formData.append("VendorName", form.vendorName.trim());
      formData.append("GSTIN", form.gstin?.trim() || "");
      formData.append("Address", form.address?.trim() || "");
      formData.append("StateName", form.stateName?.trim() || "");
      formData.append("StateCode", form.stateCode?.trim() || "");
      formData.append("Mobile", form.mobile?.trim() || "");
      formData.append("EmailId", form.emailId?.trim() || "");
      formData.append("OpBal", form.opBal || "0");

     const res = await customFetch('/VendorDT', {
        method: "POST", body: formData
      });

      if (!res.ok) throw new Error(await res.text());
      setSuccess("Vendor added successfully!");
      setForm({ vendorName: "", gstin: "", address: "", stateName: "", stateCode: "", mobile: "", emailId: "", opBal: "" });
      loadVendors();
      
      // Refocus first input after save
      formRef.current?.querySelector('input')?.focus();
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const openEditModal = (vendor) => {
    setEditVendor(vendor);
    setEditForm({
      vendorName: vendor.vendorName || "", gstin: vendor.gstin || "", address: vendor.address || "",
      stateName: vendor.stateName || "", stateCode: vendor.stateCode || "", mobile: vendor.mobile || "",
      emailId: vendor.emailId || "", opBal: vendor.opBal || ""
    });
    setEditError(""); setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false); setEditVendor(null); setEditError("");
    setEditForm({ vendorName: "", gstin: "", address: "", stateName: "", stateCode: "", mobile: "", emailId: "", opBal: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      if (!/^\d*$/.test(value)) return; // non-numeric reject
      if (value.length > 10) return;    // 10+ digits reject
    }

    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.vendorName?.trim()) { setEditError("Vendor Name is required"); return; }
    setEditLoading(true);

    try {

      const formData = new FormData();
      formData.append("VendorName", editForm.vendorName.trim());
      formData.append("GSTIN", editForm.gstin?.trim() || "");
      formData.append("Address", editForm.address?.trim() || "");
      formData.append("StateName", editForm.stateName?.trim() || "");
      formData.append("StateCode", editForm.stateCode?.trim() || "");
      formData.append("Mobile", editForm.mobile?.trim() || "");
      formData.append("EmailId", editForm.emailId?.trim() || "");
      formData.append("OpBal", editForm.opBal || "0");

    const res = await customFetch(`/VendorDT/${editVendor.vid}`, {
        method: "PUT", body: formData
      });
      if (!res.ok) throw new Error(await res.text());
      loadVendors(); setSuccess("Updated Successfully"); closeEditModal();
    } catch (err) { setEditError(err.message); } finally { setEditLoading(false); }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this Vendor?")) return;
    try {
      const res = await customFetch(`/VendorDT/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await res.text());
      setVendors(prev => prev.filter(v => v.vid !== id)); setSuccess("Deleted Successfully");
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="vendor-container">
      <div className="vendor-card">
        <div className="vendor-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-people-fill"></i></div>
            <div className="header-text"><h5>Vendor Master</h5><p>Add and manage vendors</p></div>
          </div>
        </div>

        <div className="card-content">
          {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-circle-fill"></i> {error}</div>}
          {success && <div className="alert-custom alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          {/* ADD FORM */}
          <div className="form-section">
            <div className="section-title"><i className="bi bi-person-plus"></i><span>Add New Vendor</span></div>
            <div className="section-content">
                <form ref={formRef} onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">Vendor Name <span className="required">*</span></label>
                            <input name="vendorName" value={form.vendorName} onChange={handleChange} className="form-input" required disabled={loading} placeholder="Enter Name"/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">GSTIN</label>
                            <input name="gstin" value={form.gstin} onChange={handleChange} className="form-input" disabled={loading} placeholder="GSTIN"/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Mobile</label>
                            <input name="mobile" value={form.mobile} maxLength={10} onChange={handleChange} className="form-input" disabled={loading} placeholder="Mobile"/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Op. Bal (₹)</label>
                            <input type="number" step="0.01" name="opBal" value={form.opBal} onChange={handleChange} className="form-input" disabled={loading} placeholder="0.00"/>
                        </div>
                        
                        <div className="form-group span-2">
                            <label className="form-label">Address</label>
                            <input name="address" value={form.address} onChange={handleChange} className="form-input" disabled={loading} placeholder="Address"/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">State</label>
                            <input name="stateName" value={form.stateName} onChange={handleChange} className="form-input" disabled={loading} placeholder="State Name"/>
                        </div>
                        <div className="form-group">
                            <label className="form-label">State Code</label>
                            <input name="stateCode" value={form.stateCode} onChange={handleChange} className="form-input" disabled={loading} placeholder="Code"/>
                        </div>
                        <div className="form-group span-2">
                            <label className="form-label">Email</label>
                            <input name="emailId" type="email" value={form.emailId} onChange={handleChange} className="form-input" disabled={loading} placeholder="Email Address"/>
                        </div>
                    </div>
                    <div className="form-footer">
                        <button type="submit" className="btn-submit" disabled={loading}>
                           {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus-lg"></i>} 
                           {loading ? " Saving..." : " Add Vendor"}
                        </button>
                    </div>
                </form>
            </div>
          </div>

          {/* VENDOR TABLE */}
          <div className="list-section">
             <div className="section-title"><i className="bi bi-list-ul"></i><span>Vendor List ({vendors.length})</span></div>
             <div className="section-content p-0">
                <div className="table-responsive">
                    <table className="custom-table">
                        <thead>
                           <tr>
                              <th className="col-sr">Sr</th>
                              <th>Vendor Name</th>
                              <th>GSTIN</th>
                              <th>Mobile</th>
                              <th className="text-right">Op. Bal (₹)</th>
                              <th className="text-center col-actions">Actions</th>
                           </tr>
                        </thead>
                        <tbody>
                            {vendors.length === 0 ? <tr><td colSpan="6" className="empty-state">No vendors found.</td></tr> : vendors.map((v, i) => (
                                <tr key={v.vid}>
                                    <td className="col-sr text-muted fw-600">{i + 1}</td>
                                    <td className="fw-500">{v.vendorName}</td>
                                    <td>{v.gstin || '-'}</td>
                                    <td>{v.mobile || '-'}</td>
                                    <td className="text-right fw-600 text-success">{v.opBal?.toFixed(2) || '0.00'}</td>
                                    <td className="text-center col-actions">
                                        <div className="btn-group-actions">
                                            <button className="btn-icon btn-edit" onClick={() => openEditModal(v)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                            <button className="btn-icon btn-delete" onClick={() => deleteVendor(v.vid)} title="Delete"><i className="bi bi-trash"></i></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <>
          <div className="modal-overlay" onClick={closeEditModal}></div>
          <div className="modal-container">
            <div className="modal-card">
              <div className="modal-header">
                <div className="modal-title"><i className="bi bi-pencil-square"></i><span>Edit Vendor</span></div>
                <button className="btn-modal-close" onClick={closeEditModal}><i className="bi bi-x-lg"></i></button>
              </div>
              <form ref={editFormRef} onSubmit={saveEdit}>
                <div className="modal-body">
                  {editError && <div className="alert-custom alert-danger mb-2"><i className="bi bi-exclamation-circle-fill"></i> {editError}</div>}
                  
                  <div className="form-grid">
                      <div className="form-group span-2">
                         <label className="form-label">Name <span className="required">*</span></label>
                         <input name="vendorName" value={editForm.vendorName} onChange={handleEditChange} className="form-input" required disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">GSTIN</label>
                         <input name="gstin" value={editForm.gstin} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Address</label>
                         <input name="address" value={editForm.address} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Mobile</label>
                         <input name="mobile" value={editForm.mobile} maxLength={10} onChange={handleEditChange} className="form-input" disabled={editLoading} />
                      </div>
                      <div className="form-group">
                         <label className="form-label">State</label>
                         <input name="stateName" value={editForm.stateName} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group">
                         <label className="form-label">Code</label>
                         <input name="stateCode" value={editForm.stateCode} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-2">
                         <label className="form-label">Op. Bal</label>
                         <input type="number" step="0.01" name="opBal" value={editForm.opBal} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                      <div className="form-group span-4">
                         <label className="form-label">Email</label>
                         <input name="emailId" type="email" value={editForm.emailId} onChange={handleEditChange} className="form-input" disabled={editLoading}/>
                      </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-modal-cancel" onClick={closeEditModal} disabled={editLoading}>Cancel</button>
                  <button type="submit" className="btn-modal-submit" disabled={editLoading}>{editLoading ? "Updating..." : "Update Vendor"}</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* COMPACT STYLES */}
      <style jsx>{`
        .vendor-container { padding: 0; font-family: system-ui, -apple-system, sans-serif; }
        .vendor-card { background: white; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #e2e8f0; overflow: hidden; }

        /* Header */
        .vendor-header { background: var(--theme-color, #0088cc); padding: 8px 12px; border-bottom: 2px solid #00d4aa; }
        .header-left { display: flex; align-items: center; gap: 8px; color: white; }
        .header-icon { width: 28px; height: 28px; background: rgba(255,255,255,0.2); border-radius: 4px; display: flex; justify-content: center; align-items: center; font-size: 14px; }
        .header-text h5 { margin: 0; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; font-size: 10px; color: rgba(255,255,255,0.85); }

        .card-content { padding: 12px; }

        /* Alerts */
        .alert-custom { margin-bottom: 10px; padding: 6px 10px; border-radius: 4px; font-size: 11px; font-weight: 500; display: flex; align-items: center; gap: 6px; }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }

        /* Sections */
        .form-section, .list-section { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; margin-bottom: 12px; }
        .section-title { background: var(--theme-color, #3182ce); padding: 6px 10px; color: white; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .section-content { padding: 10px; background: white; }
        .p-0 { padding: 0 !important; }

        /* Form Grid */
        .form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .span-2 { grid-column: span 2; }
        .span-4 { grid-column: span 4; }
        
        .form-label { font-size: 11px; font-weight: 600; color: #4a5568; display: flex; align-items: center; gap: 4px; }
        .required { color: #e53e3e; }

        .form-input { width: 100%; padding: 6px 8px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 12px; outline: none; transition: 0.2s; background: white; height: 30px; }
        .form-input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        .form-footer { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: right; }
        .btn-submit { padding: 0 16px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; transition: 0.2s; height: 30px; display: inline-flex; align-items: center; gap: 6px; }
        .btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Table */
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .custom-table th { background: #edf2f7; color: #4a5568; padding: 8px 10px; font-weight: 600; border-bottom: 1px solid #cbd5e0; white-space: nowrap; text-transform: uppercase; }
        .custom-table td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; color: #2d3748; vertical-align: middle; }
        .custom-table tr:hover { background: #f8fafc; }
        
        .col-sr { width: 40px; text-align: center; }
        .col-actions { width: 80px; }
        .fw-500 { font-weight: 500; }
        .fw-600 { font-weight: 600; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .text-muted { color: #64748b; }
        .text-success { color: #2f855a; }

        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        .btn-icon { width: 24px; height: 24px; border: none; border-radius: 3px; display: inline-flex; justify-content: center; align-items: center; cursor: pointer; font-size: 12px; transition: 0.2s; }
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }
        .empty-state { text-align: center; padding: 20px; color: #94a3b8; font-style: italic; }

        /* Modal Compact */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1040; animation: fadeIn 0.2s; }
        .modal-container { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1050; width: 90%; max-width: 500px; animation: slideUp 0.2s; }
        .modal-card { background: white; border-radius: 6px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
        
        .modal-header { background: var(--theme-color, #3182ce); padding: 8px 12px; color: white; display: flex; justify-content: space-between; align-items: center; }
        .modal-title { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .btn-modal-close { background: none; border: none; color: white; cursor: pointer; font-size: 14px; opacity: 0.8; display: flex; align-items: center; justify-content: center;}
        .btn-modal-close:hover { opacity: 1; }
        
        .modal-body { padding: 12px; }
        .modal-footer { padding: 8px 12px; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 6px; }
        
        .btn-modal-cancel { padding: 6px 12px; border: none; background: #edf2f7; color: #4a5568; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; }
        .btn-modal-cancel:hover { background: #e2e8f0; }
        .btn-modal-submit { padding: 6px 12px; border: none; background: var(--theme-color, #3182ce); color: white; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px;}
        .btn-modal-submit:hover:not(:disabled) { opacity: 0.9; }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translate(-50%, -40%); } to { opacity: 1; transform: translate(-50%, -50%); } }

        /* Responsive Breakpoints */
        @media (max-width: 768px) {
          .form-grid { grid-template-columns: 1fr 1fr; }
          .span-4 { grid-column: span 2; }
        }
        @media (max-width: 480px) {
          .form-grid { grid-template-columns: 1fr; }
          .span-2, .span-4 { grid-column: span 1; }
          .btn-submit { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default VendorDTForm;